import axios from 'axios';
import api from './index';
import { apiUrl } from '../config';
import logger from '../services/Logger';

const API_BASE_URL = apiUrl;

/**
 * Fetch all providers for an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of providers
 */
export const listProviders = async (orgId) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/list_providers',
      data: { org_id: orgId },
    });
    return response.data.providers || [];
  } catch (error) {
    logger.error('providers', 'Error fetching providers', { error: error.message, orgId });
    throw error;
  }
};

export const createProviderFromDraft = async (
  providerData,
  { scopes, clientId, clientSecret, icon = null }
) => {
  const formData = new FormData();
  formData.append('provider_data', JSON.stringify(providerData));
  formData.append('scopes_content', scopes);
  formData.append('client_id', clientId);
  formData.append('client_secret', clientSecret);
  if (icon) formData.append('icon', icon);
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/create_provider_from_draft',
      data: formData,
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error creating provider from drafts', { error: error.message });
    throw error;
  }
};

export const updateProviderFromDraft = async (
  providerId,
  providerData,
  { scopes, expectedUpdatedAt = null, iconAction = 'retain', icon = null }
) => {
  const formData = new FormData();
  formData.append('provider_id', providerId);
  formData.append('provider_data', JSON.stringify(providerData));
  formData.append('scopes_content', scopes);
  if (expectedUpdatedAt) formData.append('expected_updated_at', expectedUpdatedAt);
  formData.append('icon_action', iconAction);
  if (icon) formData.append('icon', icon);
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/update_provider_from_draft',
      data: formData,
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error updating provider from drafts', { error: error.message, providerId });
    throw error;
  }
};

/**
 * Delete a provider
 * Organization and actor identity are derived from the authenticated session.
 * @param {string} providerId - Provider ID to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteProvider = async (providerId) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/delete_provider',
      data: { provider_id: providerId }
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error deleting provider', { error: error.message, providerId });
    throw error;
  }
};

/**
 * Retrieve masked OAuth app credentials for a provider.
 * Returns client_id in full and last 4 chars of client_secret only.
 * @param {string} orgId - Organization ID
 * @param {string} userId - Admin user ID
 * @param {string} providerId - Provider document ID
 * @returns {Promise<{has_credentials: boolean, client_id: string|null, last4_secret: string|null}>}
 */
export const getProviderCredentials = async (orgId, userId, providerId) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/get_provider_credentials',
      data: {
        org_id: orgId,
        uid: userId,
        provider_id: providerId,
      },
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error retrieving provider credentials', { error: error.message, orgId, providerId });
    throw error;
  }
};

/**
 * Store OAuth app credentials (CLIENT_ID / CLIENT_SECRET) for a provider.
 * Encrypts credentials into a Provider ATOM via GCCS.
 * @param {string} orgId - Organization ID
 * @param {string} userId - Admin user ID
 * @param {string} providerId - Provider document ID
 * @param {string} clientId - OAuth app CLIENT_ID
 * @param {string} clientSecret - OAuth app CLIENT_SECRET
 * @returns {Promise<Object>} Result
 */
export const saveProviderCredentials = async (orgId, userId, providerId, clientId, clientSecret) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/save_provider_credentials',
      data: {
        org_id: orgId,
        uid: userId,
        provider_id: providerId,
        client_id: clientId,
        client_secret: clientSecret,
      },
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error saving provider credentials', { error: error.message, orgId, providerId });
    throw error;
  }
};

/**
 * Fetch all agents for an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of agents
 */
export const listAgents = async (orgId, { uid, includeDisabled = false } = {}) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/list_agents',
      data: {
        org_id: orgId,
        ...(includeDisabled ? { uid, include_disabled: true } : {}),
      },
    });
    return response.data.agents || [];
  } catch (error) {
    logger.error('providers', 'Error fetching agents', { error: error.message, orgId });
    throw error;
  }
};

export const discoverAgentTools = async (connection) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/tools/discover',
      data: connection,
    });
    return response.data.tools || [];
  } catch (error) {
    logger.error('providers', 'Error discovering agent tools', { error: error.message });
    throw error;
  }
};

export const convertHTTPAPISchema = async (payload, { signal } = {}) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/http/convert',
      data: payload,
      ...(signal ? { signal } : {}),
    });
    const result = response.data;
    if (
      result?.schema_version !== 1 || result.draft !== true
      || result.base_uri !== payload.base_uri.trim()
      || !Array.isArray(result.tools) || result.tools.length === 0
      || !Array.isArray(result.http_operations)
      || result.tools.length !== result.http_operations.length
      || result.tools.some((tool, index) => (
        typeof tool?.name !== 'string' || !tool.name
        || typeof tool.description !== 'string'
        || tool.inputSchema?.type !== 'object'
        || tool.inputSchema.additionalProperties !== false
        || tool.name !== result.http_operations[index]?.name
        || JSON.stringify(tool.inputSchema) !== JSON.stringify(result.http_operations[index]?.input_schema)
        || typeof result.http_operations[index]?.request?.path !== 'string'
        || !Array.isArray(result.http_operations[index]?.request?.parameters)
        || !['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'].includes(result.http_operations[index]?.request?.method)
      ))
    ) {
      throw new Error('The backend returned an invalid HTTP conversion result.');
    }
    return result;
  } catch (error) {
    if (error.code !== 'ERR_CANCELED') {
      logger.error('providers', 'HTTP schema conversion failed', {
        code: error.code,
        status: error.response?.status,
      });
    }
    throw error;
  }
};

export const generateAgentPrompt = async (context) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/prompt/generate',
      data: context,
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error generating agent prompt', { error: error.message });
    throw error;
  }
};

export const createAgentFromDraft = async (
  agentData,
  {
    policy,
    tools,
    prompt,
    icon = null,
    connectionType = 'mcp',
    connectionSource = null,
  }
) => {
  try {
    const formData = new FormData();
    formData.append('agent_data', JSON.stringify(agentData));
    formData.append('policy_content', policy);
    formData.append('tools_content', tools);
    formData.append('prompt_content', prompt);
    formData.append('connection_type', connectionType);
    if (connectionSource) formData.append('connection_source', JSON.stringify(connectionSource));
    if (icon) formData.append('icon', icon);

    const response = await api({
      method: 'POST',
      url: '/agent/create_agent_from_draft',
      data: formData,
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error creating agent from drafts', { error: error.message });
    throw error;
  }
};

export const updateAgentFromDraft = async (
  agentId,
  agentData,
  {
    policy,
    tools,
    prompt,
    expectedUpdatedAt = null,
    iconAction = 'retain',
    icon = null,
    connectionType = 'mcp',
    connectionSource = null,
    connectionAction = 'retain',
  }
) => {
  try {
    const formData = new FormData();
    formData.append('agent_id', agentId);
    formData.append('agent_data', JSON.stringify(agentData));
    formData.append('policy_content', policy);
    formData.append('tools_content', tools);
    formData.append('prompt_content', prompt);
    if (expectedUpdatedAt) formData.append('expected_updated_at', expectedUpdatedAt);
    formData.append('icon_action', iconAction);
    formData.append('connection_type', connectionType);
    formData.append('connection_action', connectionAction);
    if (connectionSource) formData.append('connection_source', JSON.stringify(connectionSource));
    if (icon) formData.append('icon', icon);

    const response = await api({
      method: 'POST',
      url: '/agent/update_agent_from_draft',
      data: formData,
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error updating agent from drafts', { error: error.message, agentId });
    throw error;
  }
};

/**
 * Delete an agent
 * @param {string} agentId - Agent ID to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteAgent = async (agentId) => {
  try {
    const response = await api({
      method: 'POST',
      url: '/agent/delete_agent',
      data: { agent_id: agentId }
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error deleting agent', { error: error.message, agentId });
    throw error;
  }
};

/**
 * Generate OAuth URL for a provider
 * @param {string} providerId - Provider ID (e.g., 'google_workspace', 'hubspot')
 * @param {string} uid - User ID
 * @param {string} orgId - Organization ID
 * @param {{returnTo?: string, chatId?: string, spaceId?: string}} returnContext
 * @returns {string} OAuth URL
 */
export const getProviderOAuthUrl = async (providerId, uid, orgId, returnContext = {}) => {
  const params = new URLSearchParams({ uid, org_id: orgId });
  params.set("return_url", "true");
  if (returnContext.returnTo) params.set("return_to", returnContext.returnTo);
  if (returnContext.chatId) params.set("chat_id", returnContext.chatId);
  if (returnContext.spaceId) params.set("space_id", returnContext.spaceId);
  const response = await api({
    method: "GET",
    url: `/oauth/${encodeURIComponent(providerId)}?${params.toString()}`,
  });
  return response.data.authorization_url;
};

/**
 * Disconnect OAuth provider for user
 * @param {string} providerId - Provider ID
 * @param {string} uid - User ID
 * @param {string} orgId - Organization ID
 * @returns {Promise} Disconnect response
 */
export const disconnectProvider = async (providerId, uid, orgId) => {
  const response = await api({
    method: "DELETE",
    url: `/oauth/${encodeURIComponent(providerId)}/disconnect`,
    params: { uid, org_id: orgId },
  });
  return response.data;
};

/**
 * Check if user is authenticated with a provider
 * @param {Object} userDetails - User details object with Identity ATOMs
 * @param {string} providerId - Provider instance identifier
 * @returns {boolean} True if authenticated
 */
export const isProviderAuthenticated = (userDetails, providerId) => {
  if (!userDetails?.identity_atoms?.[providerId]) return false;
  return userDetails?.provider_auth_state?.[providerId] === 'connected';
};

/**
 * Get user details including atoms via API
 * @param {string} userId - User ID
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} User details with atoms
 */
export const getUserDetailsAPI = async (userId, orgId) => {
  const response = await api({
    method: "GET",
    url: `/oauth/user/${encodeURIComponent(userId)}/details`,
    params: { org_id: orgId },
  });
  return response.data;
};

/**
 * Upload a file to GCS storage
 * @param {File} file - The file to upload
 * @param {string} orgId - Organization ID
 * @param {string} uploadType - Type of upload: 'provider_icon', 'agent_icon', 'scopes', 'agent_policy', 'agent_tools', 'agent_prompt'
 * @param {string} entityId - The provider_id or agent_id
 * @param {string} providerId - Optional provider_id for agent file uploads (required for agent_policy, agent_tools, agent_prompt)
 * @returns {Promise<Object>} Upload result with file_path
 */
export const uploadFile = async (file, orgId, uploadType, entityId, providerId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('org_id', orgId);
    formData.append('upload_type', uploadType);
    formData.append('entity_id', entityId);
    if (providerId) {
      formData.append('provider_id', providerId);
    }

    const response = await axios.post(`${API_BASE_URL}/storage/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error uploading file', { error: error.message, uploadType, entityId });
    throw error;
  }
};

/**
 * Download/read file content from GCS
 * @param {string} filePath - The GCS file path
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} File content
 */
export const readFileContent = async (filePath, orgId) => {
  try {
    const response = await api({
      method: 'GET',
      url: '/storage/download',
      params: {
        file_path: filePath,
        org_id: orgId,
      },
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error reading file', { error: error.message, filePath });
    throw error;
  }
};

/**
 * Save text content directly to GCS
 * @param {string} filePath - The GCS file path
 * @param {string} orgId - Organization ID
 * @param {string} content - The text content to save
 * @param {string} contentType - MIME type (default: application/json)
 * @returns {Promise<Object>} Save result
 */
export const saveFileContent = async (filePath, orgId, content, contentType = 'application/json') => {
  try {
    const formData = new FormData();
    formData.append('file_path', filePath);
    formData.append('org_id', orgId);
    formData.append('content', content);
    formData.append('content_type', contentType);

    const response = await api({
      method: 'POST',
      url: '/storage/save-content',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Error saving file', { error: error.message, filePath });
    throw error;
  }
};

export const downloadImageBlob = async (filePath, orgId) => {
  const response = await api({
    method: 'GET',
    url: '/storage/download-image',
    params: {
      file_path: filePath,
      org_id: orgId,
    },
    responseType: 'blob',
  });
  return response.data;
};

// Skills API

export const listSkills = async (orgId) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/list", data: { org_id: orgId } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to list skills', { error: error.message });
    throw error;
  }
};

export const installSkill = async (orgId, uid, command, selectedSkills, skillScopes) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/install", data: {
      org_id: orgId,
      uid,
      command,
      ...(selectedSkills ? { selected_skills: selectedSkills } : {}),
      ...(skillScopes && Object.keys(skillScopes).length > 0 ? { skill_scopes: skillScopes } : {}),
    } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to install skill', { error: error.message });
    throw error;
  }
};

export const updateSkill = async (orgId, uid, skillId, updates) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/update", data: { org_id: orgId, uid, skill_id: skillId, ...updates } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to update skill', { error: error.message });
    throw error;
  }
};

export const removeSkill = async (orgId, uid, skillId, force = false) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/remove", data: { org_id: orgId, uid, skill_id: skillId, force } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to remove skill', { error: error.message });
    throw error;
  }
};

export const previewSkill = async (command) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/preview", data: { command } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to preview skill', { error: error.message });
    throw error;
  }
};

export const fetchSkillMd = async (orgId, skillId) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/skill-md", data: { org_id: orgId, skill_id: skillId } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to fetch skill md', { error: error.message });
    throw error;
  }
};

export const getSkillCredential = async (orgId, uid, skillId) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/credential/get", data: { org_id: orgId, uid, skill_id: skillId } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to get skill credential', { error: error.message });
    throw error;
  }
};

export const saveSkillCredential = async (orgId, uid, skillId, apiKey) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/credential/save", data: { org_id: orgId, uid, skill_id: skillId, api_key: apiKey } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to save skill credential', { error: error.message });
    throw error;
  }
};

export const saveAgentSkills = async (orgId, uid, providerId, agentId, skills) => {
  try {
    const response = await api({ method: "POST", url: "/agent/skills/save-agent-skills", data: {
      org_id: orgId,
      uid,
      provider_id: providerId,
      agent_id: agentId,
      skills,
    } });
    return response.data;
  } catch (error) {
    logger.error('providers', 'Failed to save agent skills', { error: error.message });
    throw error;
  }
};
